import { useState } from 'react';
import { createTranslator } from '@zt/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Mail, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { LocalePicker, useContentLocale } from '../components/ContentLocale';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { FormDefinition, FormSubmissionList } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { ConvertTicketDialog } from './support';

/**
 * Formlar (F5B-20, ADR-077): iletişim formunun alanları ve bayilik başvurusunun ek alanları; iletişim
 * mesajları ayrı ekranda (müşteri yetkisiyle).
 */

type Kind = FormDefinition['kind'];
type FieldDef = FormDefinition['fields'][number];
const KINDS: Kind[] = ['contact', 'dealer_application'];
const TYPES: FieldDef['type'][] = [
  'text',
  'textarea',
  'email',
  'tel',
  'number',
  'select',
  'checkbox',
  'date',
];

const newFieldId = () => `alan_${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------- Form düzenleyici

export function FormsPage() {
  const t = useT();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const [kind, setKind] = useState<Kind>('contact');
  const def = useQuery({
    queryKey: ['form-definition', storeId, kind, locale],
    queryFn: () =>
      api.get<FormDefinition>(`/v1/admin/forms?storeId=${storeId}&kind=${kind}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.forms">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
            <Field label={t('admin.forms.form')} help={<HelpTip k="forms.kind" />} className="w-64">
              {(p) => (
                <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`admin.forms.kinds.${k}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {!def.data ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <FormEditor
              key={`${def.data.storeId}-${def.data.kind}-${def.data.locale}-${def.data.version}`}
              def={def.data}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

function FormEditor({ def }: { def: FormDefinition }) {
  const t = useT();
  const { can } = usePermissions();
  const editable = can('content.publish');
  const [fields, setFields] = useState<FieldDef[]>(def.fields);
  const [enabled, setEnabled] = useState(def.enabled);
  const [notifyStaff, setNotifyStaff] = useState(def.notifyStaff);
  const [dirty, setDirty] = useState(false);
  const save = useApiMutation(
    () =>
      api.put<FormDefinition>('/v1/admin/forms', {
        storeId: def.storeId,
        kind: def.kind,
        locale: def.locale,
        fields,
        enabled,
        notifyStaff,
        version: def.version,
      }),
    { invalidate: [['form-definition', def.storeId, def.kind]], success: t('admin.forms.saved') },
  );
  const update = (i: number, patch: Partial<FieldDef>) => {
    setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
    setDirty(true);
  };
  const move = (i: number, d: -1 | 1) => {
    setFields((fs) => {
      const next = [...fs];
      [next[i], next[i + d]] = [next[i + d]!, next[i]!];
      return next;
    });
    setDirty(true);
  };
  const contact = def.kind === 'contact';
  // Tek alana bağlı olmayan hatalar (ör. e-posta alanı yok, aydınlatma metni yayımda değil).
  const general =
    save.error instanceof ApiError
      ? save.error.errors.find((e) => e.field === 'fields' || e.field === 'enabled')?.message
      : undefined;
  return (
    <>
      {contact ? (
        <Section title={t('admin.forms.settingsTitle')} className="mt-6">
          <div className="space-y-4">
            {!def.privacyNotice && (
              <p className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="amber">{t('admin.forms.noticeMissing')}</Badge>
                <span className="text-ink-2">{t('admin.forms.noticeMissingLead')}</span>
                <Link to="/yasal-metinler" className="text-pine underline underline-offset-2">
                  {t('admin.nav.legal')}
                </Link>
              </p>
            )}
            <div className="flex items-center gap-3">
              <Switch
                id="form-enabled"
                checked={enabled}
                disabled={!editable || (!def.privacyNotice && !enabled)}
                onCheckedChange={(v) => {
                  setEnabled(v);
                  setDirty(true);
                }}
                label={t('admin.forms.enabled')}
              />
              <label htmlFor="form-enabled" className="text-sm font-medium">
                {t('admin.forms.enabled')}
              </label>
              <HelpTip k="forms.enabled" />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="form-notify"
                checked={notifyStaff}
                disabled={!editable}
                onCheckedChange={(v) => {
                  setNotifyStaff(v);
                  setDirty(true);
                }}
                label={t('admin.forms.notifyStaff')}
              />
              <label htmlFor="form-notify" className="text-sm font-medium">
                {t('admin.forms.notifyStaff')}
              </label>
              <HelpTip k="forms.notify" />
            </div>
            {def.newSubmissions !== null && (
              <p className="flex items-center gap-2 text-sm text-ink-2">
                <Mail className="h-4 w-4" aria-hidden />
                <Link to="/iletisim-mesajlari" className="text-pine hover:underline">
                  {t('admin.forms.newMessages', { count: def.newSubmissions })}
                </Link>
              </p>
            )}
          </div>
        </Section>
      ) : (
        <p className="mt-6 max-w-[62ch] text-sm text-ink-2">{t('admin.forms.dealerLead')}</p>
      )}
      <Section
        title={contact ? t('admin.forms.fieldsTitle') : t('admin.forms.extraFieldsTitle')}
        description={t('admin.forms.fieldsLead')}
        action={<HelpTip k="forms.fields" />}
      >
        {!fields.length ? (
          <p className="mb-3 text-sm text-ink-2">{t('admin.forms.noFields')}</p>
        ) : (
          <ol className="space-y-3">
            {fields.map((f, i) => (
              <FieldEditor
                key={f.id}
                field={f}
                index={i}
                count={fields.length}
                editable={editable}
                contentLocale={def.locale}
                error={(k) => fieldError(save.error, `fields.${i}${k ? `.${k}` : ''}`)}
                onChange={(patch) => update(i, patch)}
                onMove={(d) => move(i, d)}
                onRemove={() => {
                  setFields((fs) => fs.filter((_, j) => j !== i));
                  setDirty(true);
                }}
              />
            ))}
          </ol>
        )}
        {general && (
          <p role="alert" className="mt-3 text-sm font-medium text-rose">
            {general}
          </p>
        )}
        {editable && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => {
                setFields((fs) => [
                  ...fs,
                  {
                    id: newFieldId(),
                    type: 'text',
                    label: '',
                    help: null,
                    required: false,
                    options: [],
                    preset: null,
                  },
                ]);
                setDirty(true);
              }}
              disabled={fields.length >= 20}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.forms.addField')}
            </Button>
            <HelpTip k="forms.add_field" />
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!dirty}
              onClick={() => save.mutate(undefined)}
            >
              {t('admin.forms.save')}
            </Button>
            <HelpTip k="forms.save" />
          </div>
        )}
      </Section>
    </>
  );
}

function FieldEditor({
  field: f,
  index,
  count,
  editable,
  contentLocale,
  error,
  onChange,
  onMove,
  onRemove,
}: {
  field: FieldDef;
  index: number;
  count: number;
  editable: boolean;
  contentLocale: string;
  error: (k?: string) => string | undefined;
  onChange: (patch: Partial<FieldDef>) => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  const t = useT();
  // Etiket boşsa vitrinde görünecek varsayılan etiket (formun dilinde).
  const presetLabel = f.preset
    ? createTranslator(contentLocale)(`storefront.contactForm.presets.${f.preset}`)
    : '';
  const name = f.label || presetLabel || t('admin.forms.untitled');
  // Hazır e-posta alanı yanıt için gerekir: türü ve zorunluluğu değişmez, silinmez.
  const locked = f.preset === 'email';
  return (
    <li className="rounded-md border border-line bg-surface px-4 py-3">
      <fieldset disabled={!editable}>
        <legend className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          {t('admin.forms.fieldN', { n: index + 1, name })}
          {f.required && <Badge tone="pine">{t('admin.forms.requiredBadge')}</Badge>}
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.forms.label')}
            optionalLabel={f.preset ? t('common.optional') : undefined}
            help={<HelpTip k="forms.label" />}
            error={error('label')}
          >
            {(p) => (
              <Input
                {...p}
                value={f.label}
                maxLength={80}
                placeholder={presetLabel || undefined}
                onChange={(e) => onChange({ label: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.forms.type')} help={<HelpTip k="forms.type" />}>
            {(p) => (
              <Select
                {...p}
                value={f.type}
                disabled={locked || !editable}
                onChange={(e) => {
                  const type = e.target.value as FieldDef['type'];
                  onChange({ type, options: type === 'select' ? f.options : [] });
                }}
              >
                {TYPES.map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.forms.types.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.forms.help')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="forms.help_text" />}
            error={error('help')}
            className="sm:col-span-2"
          >
            {(p) => (
              <Input
                {...p}
                value={f.help ?? ''}
                maxLength={200}
                onChange={(e) => onChange({ help: e.target.value || null })}
              />
            )}
          </Field>
          {f.type === 'select' && (
            <Field
              label={t('admin.forms.options')}
              help={<HelpTip k="forms.options" />}
              error={error('options')}
              className="sm:col-span-2"
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={4}
                  value={f.options.join('\n')}
                  onChange={(e) =>
                    onChange({ options: e.target.value.split('\n').map((o) => o.trimStart()) })
                  }
                  onBlur={() => onChange({ options: f.options.map((o) => o.trim()).filter(Boolean) })}
                />
              )}
            </Field>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="me-auto flex items-center gap-2">
            <Switch
              id={`req-${f.id}`}
              checked={f.required}
              disabled={locked || !editable}
              onCheckedChange={(v) => onChange({ required: v })}
              label={t('admin.forms.required')}
            />
            <label htmlFor={`req-${f.id}`} className="text-sm">
              {t('admin.forms.required')}
            </label>
            <HelpTip k="forms.required" />
          </div>
          {error() && !error('label') && !error('options') && (
            <span role="alert" className="text-sm font-medium text-rose">
              {error()}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t('admin.forms.moveUp', { name })}
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label={t('admin.forms.moveDown', { name })}
          >
            <ArrowDown className="h-4 w-4" aria-hidden />
          </Button>
          {!locked && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemove}
              aria-label={t('admin.forms.remove', { name })}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </fieldset>
    </li>
  );
}

// ---------------------------------------------------------------------------- İletişim mesajları

type Status = 'new' | 'handled' | 'all';

export function ContactMessagesPage() {
  const t = useT();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [status, setStatus] = useState<Status>('new');
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: ['form-submissions', storeId, status, page],
    queryFn: () =>
      api.get<FormSubmissionList>(
        `/v1/admin/form-submissions?storeId=${storeId}&status=${status}&page=${page}`,
      ),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  return (
    <PageFrame helpKey="page.contact_messages">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <Field label={t('admin.messages.filter')} help={<HelpTip k="messages.filter" />} className="w-56">
              {(p) => (
                <Select
                  {...p}
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as Status);
                    setPage(1);
                  }}
                >
                  {(['new', 'handled', 'all'] as const).map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.messages.filters.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {d && d.newCount > 0 && (
              <Badge tone="amber">{t('admin.messages.newCount', { count: d.newCount })}</Badge>
            )}
          </div>
          <Section title={t('admin.messages.listTitle')} className="mt-6">
            {!d ? (
              <Spinner label={t('common.loading')} />
            ) : !d.items.length ? (
              <EmptyState title={t('admin.messages.empty')} description={t('admin.messages.emptyLead')} />
            ) : (
              <ul className="space-y-3" aria-label={t('admin.messages.listTitle')}>
                {d.items.map((m) => (
                  <MessageItem key={m.id} m={m} pii={d.pii} />
                ))}
              </ul>
            )}
            {d && pages > 1 && (
              <div className="mt-4 flex items-center gap-3 text-sm">
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  {t('common.previous')}
                </Button>
                <span className="text-ink-2">{t('admin.messages.pageOf', { page, pages })}</span>
                <Button size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  {t('common.next')}
                </Button>
              </div>
            )}
          </Section>
        </>
      )}
    </PageFrame>
  );
}

function MessageItem({ m, pii }: { m: FormSubmissionList['items'][number]; pii: boolean }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const mark = useApiMutation(
    (status: 'new' | 'handled') =>
      api.post(`/v1/admin/form-submissions/${m.id}/status`, { status, version: m.version }),
    { invalidate: [['form-submissions'], ['form-definition']], success: t('common.saved') },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/form-submissions/${m.id}`), {
    invalidate: [['form-submissions'], ['form-definition']],
    success: t('admin.messages.deleted'),
  });
  const [converting, setConverting] = useState(false);
  const label = (a: (typeof m.answers)[number]) =>
    a.label || (a.preset ? t(`admin.forms.presets.${a.preset}`) : a.id);
  const value = (a: (typeof m.answers)[number]) =>
    a.value === null || a.value === ''
      ? '—'
      : a.type === 'checkbox'
        ? a.value
          ? t('common.yes')
          : t('common.no')
        : String(a.value);
  return (
    <li className="rounded-md border border-line bg-surface px-4 py-3 text-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <time dateTime={m.createdAt} className="font-medium text-ink">
          {formatDate(m.createdAt, true)}
        </time>
        <Badge tone={m.status === 'new' ? 'amber' : 'moss'}>{t(`admin.messages.statuses.${m.status}`)}</Badge>
      </div>
      <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-[minmax(8rem,auto)_1fr]">
        {m.answers.map((a) => (
          <div key={a.id} className="contents">
            <dt className="text-ink-3">{label(a)}</dt>
            <dd className="whitespace-pre-line break-words text-ink">{value(a)}</dd>
          </div>
        ))}
      </dl>
      {can('customers.customer.write') && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {pii && m.email && (
            <a className="text-pine underline underline-offset-2" href={`mailto:${m.email}`}>
              {t('admin.messages.reply')}
            </a>
          )}
          <Button
            size="sm"
            loading={mark.isPending}
            onClick={() => mark.mutate(m.status === 'new' ? 'handled' : 'new')}
          >
            {m.status === 'new' ? t('admin.messages.markHandled') : t('admin.messages.markNew')}
          </Button>
          <HelpTip k="messages.handle" />
          <ConfirmButton
            message={t('admin.messages.deleteConfirm')}
            onConfirm={() => remove.mutateAsync(undefined)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            {t('common.delete')}
          </ConfirmButton>
          <HelpTip k="messages.delete" />
        </div>
      )}
      {can('customers.ticket.manage') && m.email && (
        <div className="mt-2 flex items-center gap-1">
          <Button size="sm" onClick={() => setConverting(true)}>
            {t('admin.messages.convert')}
          </Button>
          <HelpTip k="messages.convert" />
        </div>
      )}
      {converting && <ConvertTicketDialog submission={m} onClose={() => setConverting(false)} />}
    </li>
  );
}
